import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, API_ENDPOINTS } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('authToken');
    const userInfo = localStorage.getItem('userInfo');
    const companyToken = localStorage.getItem('companyToken');
    const companyInfo = localStorage.getItem('companyInfo');
    const branchToken = localStorage.getItem('branchToken');
    const branchInfo = localStorage.getItem('branchInfo');
    const employeeToken = localStorage.getItem('employeeToken');
    const employeeInfo = localStorage.getItem('employeeInfo');
    
    // Check for company login first
    if (companyToken && companyInfo) {
      try {
        const parsedUser = JSON.parse(companyInfo);
        parsedUser.userType = 'company';
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing company info:', error);
        localStorage.removeItem('companyToken');
        localStorage.removeItem('companyInfo');
      }
    }
    // Check for branch login
    else if (branchToken && branchInfo) {
      try {
        const parsedUser = JSON.parse(branchInfo);
        parsedUser.userType = 'branch';
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing branch info:', error);
        localStorage.removeItem('branchToken');
        localStorage.removeItem('branchInfo');
      }
    }
    // Check for employee login
    else if (employeeToken && employeeInfo) {
      try {
        const parsedUser = JSON.parse(employeeInfo);
        parsedUser.userType = 'employee';
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing employee info:', error);
        localStorage.removeItem('employeeToken');
        localStorage.removeItem('employeeInfo');
      }
    }
    // Check for regular user login
    else if (token && userInfo) {
      try {
        const parsedUser = JSON.parse(userInfo);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing user info:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password, userType = 'employee') => {
    try {
      setLoading(true);
      
      // Use appropriate endpoint based on userType
      let loginEndpoint;
      if (userType === 'company') {
        loginEndpoint = API_ENDPOINTS.COMPANY_LOGIN;
      } else {
        loginEndpoint = API_ENDPOINTS.LOGIN;
      }
      
      const requestData = {
        email,
        password,
        userType: userType || 'employee'
      };
      
      console.log('🚀 Login Request:', {
        endpoint: loginEndpoint,
        data: { email, password: '***' },
        userType,
        baseURL: authApi.defaults.baseURL
      });
      
      const response = await authApi.post(loginEndpoint, requestData);
      
      console.log('✅ Login Response:', response.data);

      const { token, accessToken, user: userData, company: companyData } = response.data;
      
      // Clear any existing data first to prevent caching issues
      localStorage.clear();
      
      // Store token and user info based on user type
      const authToken = accessToken || token;
      const finalUserData = userData || companyData;
      
      if (userType === 'company') {
        localStorage.setItem('companyToken', authToken);
        localStorage.setItem('companyInfo', JSON.stringify(finalUserData));
        finalUserData.userType = 'company';
      } else {
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('userInfo', JSON.stringify(finalUserData));
      }
      
      console.log('🔑 Token stored for company:', finalUserData.companyId || finalUserData.id);
      console.log('👤 User data stored:', finalUserData);
      
      setUser(finalUserData);
      setIsAuthenticated(true);
      
      return { success: true, user: finalUserData };
    } catch (error) {
      console.error('❌ Login error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data,
          headers: error.config?.headers
        }
      });
      
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Login failed. Please try again.';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Clear all authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('companyToken');
    localStorage.removeItem('branchToken');
    localStorage.removeItem('employeeToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('companyInfo');
    localStorage.removeItem('branchInfo');
    localStorage.removeItem('employeeInfo');
    
    setUser(null);
    setIsAuthenticated(false);
    
    // Redirect to main login
    window.location.href = '/main-login';
  };

  const register = async (userData, userType = 'superadmin') => {
    try {
      setLoading(true);
      
      let registerEndpoint = API_ENDPOINTS.REGISTER_SUPERADMIN;
      
      const response = await authApi.post(registerEndpoint, userData);
      
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    register,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
